import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatRelative } from '@/lib/time';

type Lead = {
  ghostfit_id: string | null;
  school_name: string;
  city: string | null;
  state: string | null;
  sport_id: string;
  sport_name: string;
  head_coach: string | null;
  coach_email: string | null;
  ad_name: string | null;
  ad_email: string | null;
  logo_status: string | null;
  had_previous_fundraiser: boolean;
  last_fundraiser_at: string | null;
  total_past_units: number;
  priority: 'warm_lead' | 'has_contact' | 'cold';
};

type Payload = {
  leads: Lead[];
  count: number;
  currentMonth: number;
  state: string;
};

const STATES = ['ALL', 'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const PRIORITY_TONE: Record<Lead['priority'], string> = {
  warm_lead:   'bg-success/10 text-success',
  has_contact: 'bg-primary/10 text-primary',
  cold:        'bg-bg text-muted',
};

const PRIORITY_LABEL: Record<Lead['priority'], string> = {
  warm_lead:   'Warm Lead',
  has_contact: 'Has Contact',
  cold:        'Cold',
};

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function Outreach() {
  const [state, setState] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState<'' | Lead['priority']>('');

  // 'ALL' scans scout-dashboard's full warm cache (sports data is sparse, so
  // nationwide is the most useful default view).
  const url = `/api/outreach?limit=500${state !== 'ALL' ? `&state=${state}` : ''}`;
  const { data, isLoading } = useSWR<Payload>(url, get);

  const filtered = priorityFilter
    ? (data?.leads || []).filter((l) => l.priority === priorityFilter)
    : (data?.leads || []);

  const counts = (data?.leads || []).reduce(
    (acc, l) => { acc[l.priority] = (acc[l.priority] || 0) + 1; return acc; },
    {} as Record<string, number>
  );

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Outreach</h1>
      <p className="mb-3 text-[12px] text-muted">
        Schools with sports in current outreach window
        {data?.currentMonth ? ` · ${MONTH_NAMES[data.currentMonth]}` : ''}
        {data ? ` · ${data.count} leads ${state === 'ALL' ? 'nationwide' : `in ${state}`}` : ''}
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          value={state}
          onChange={(e) => { setState(e.target.value); setPriorityFilter(''); }}
          className="h-8 rounded-sm border border-border bg-surface px-2.5 text-[12px] text-text"
        >
          {STATES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All states' : s}</option>)}
        </select>

        {data && data.leads.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {(['', 'warm_lead', 'has_contact', 'cold'] as const).map((p) => (
              <button
                key={p || 'all'}
                onClick={() => setPriorityFilter(p)}
                className={`rounded-sm px-2.5 py-1 text-[11px] ${priorityFilter === p ? 'bg-primary text-white' : 'bg-surface text-text/75 border border-border hover:bg-bg'}`}
              >
                {p ? `${PRIORITY_LABEL[p]} · ${counts[p] || 0}` : `All · ${data.leads.length}`}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2 rounded-md bg-surface p-3 shadow-card">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md bg-surface p-8 text-center text-[12px] text-muted shadow-card">
          {data?.leads.length === 0
            ? `No schools ${state === 'ALL' ? '' : `in ${state} `}have sports in the current outreach window.`
            : `No ${priorityFilter ? PRIORITY_LABEL[priorityFilter].toLowerCase() : ''} leads ${state === 'ALL' ? 'nationwide' : `in ${state}`}.`}
        </div>
      ) : (
        <div className="rounded-md bg-surface shadow-card">
          {filtered.map((lead) => (
            <LeadRow key={`${lead.ghostfit_id || lead.school_name}:${lead.sport_id}`} lead={lead} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeadRow({ lead }: { lead: Lead }) {
  return (
    <div className="flex items-start gap-3 border-b border-divider px-3 py-2.5 last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-[12.5px] font-medium text-text">{lead.school_name}</span>
          {lead.city && <span className="shrink-0 text-[10.5px] text-muted">{lead.city}, {lead.state}</span>}
        </div>
        <div className="mt-0.5 text-[11px] text-muted">
          {lead.sport_name}
          {lead.head_coach && (
            <>
              {' · Coach '}
              {lead.coach_email ? (
                <a href={`mailto:${lead.coach_email}`} className="text-primary hover:underline">{lead.head_coach}</a>
              ) : (
                <span>{lead.head_coach}</span>
              )}
            </>
          )}
          {!lead.head_coach && lead.ad_name && (
            <>
              {' · AD '}
              {lead.ad_email ? (
                <a href={`mailto:${lead.ad_email}`} className="text-primary hover:underline">{lead.ad_name}</a>
              ) : (
                <span>{lead.ad_name}</span>
              )}
            </>
          )}
          {lead.had_previous_fundraiser && lead.last_fundraiser_at && (
            <> · last fundraiser {formatRelative(lead.last_fundraiser_at)}</>
          )}
        </div>
      </div>
      <Badge className={PRIORITY_TONE[lead.priority]}>{PRIORITY_LABEL[lead.priority]}</Badge>
    </div>
  );
}
